import { ApiResponse } from "src/models";
import type {
  BenchmarkRow,
  CreateSessionRequest,
  EntryDto,
  EntryResponse,
  PriorWindow,
  ReviewFlag,
  ReviewView,
  SessionAction,
  SessionDetail,
  SessionSummary,
  Station,
  StudentResult,
  StudentStatus,
} from "src/models/sessions";
import { apiSlice } from "src/store/apiSlice";

/** grading-service session endpoints, served at /api/grading/v1 through the gateway. */
export const sessionsApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listSessions: builder.query<
      { sessions: SessionDetail[]; total: number },
      { classId?: string; status?: string; date?: string } | void
    >({
      query: (filter) => {
        const params = new URLSearchParams();
        if (filter?.classId) params.set("classId", filter.classId);
        if (filter?.status) params.set("status", filter.status);
        if (filter?.date) params.set("date", filter.date);
        return `/api/grading/v1/sessions?${params.toString()}`;
      },
      transformResponse: (
        r: ApiResponse<{ sessions: SessionDetail[]; total: number }>,
      ) => r.data,
      providesTags: ["Sessions"],
    }),
    getSession: builder.query<SessionDetail, string>({
      query: (id) => `/api/grading/v1/sessions/${id}`,
      transformResponse: (r: ApiResponse<{ session: SessionDetail }>) =>
        r.data.session,
      providesTags: (_r, _e, id) => [{ type: "Session", id }],
    }),
    createSession: builder.mutation<
      { session: SessionDetail; warnings: string[] },
      CreateSessionRequest
    >({
      query: (body) => ({
        url: "/api/grading/v1/sessions",
        method: "POST",
        body,
      }),
      transformResponse: (
        r: ApiResponse<{ session: SessionDetail; warnings: string[] }>,
      ) => r.data,
      invalidatesTags: ["Sessions"],
    }),
    transitionSession: builder.mutation<
      { session: SessionDetail; summary?: SessionSummary },
      { id: string; action: SessionAction; reason?: string }
    >({
      query: ({ id, action, reason }) => ({
        url: `/api/grading/v1/sessions/${id}/status`,
        method: "POST",
        body: { action, reason },
      }),
      transformResponse: (
        r: ApiResponse<{ session: SessionDetail; summary?: SessionSummary }>,
      ) => r.data,
      invalidatesTags: (_r, _e, { id }) => [
        "Sessions",
        { type: "Session", id },
      ],
    }),
    setStudentStatus: builder.mutation<
      void,
      { id: string; studentId: string; status: StudentStatus }
    >({
      query: ({ id, studentId, status }) => ({
        url: `/api/grading/v1/sessions/${id}/students/${studentId}/status`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: "Session", id }],
    }),
    updateStation: builder.mutation<
      Station,
      {
        sessionId: string;
        stationId: string;
        body: Partial<
          Pick<Station, "eventId" | "label" | "locked" | "groupAssignments">
        >;
      }
    >({
      query: ({ sessionId, stationId, body }) => ({
        url: `/api/grading/v1/sessions/${sessionId}/stations/${stationId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (r: ApiResponse<{ station: Station }>) =>
        r.data.station,
      invalidatesTags: (_r, _e, { sessionId }) => [
        { type: "Session", id: sessionId },
      ],
    }),
    putEntry: builder.mutation<
      EntryResponse,
      { sessionId: string; entry: EntryDto }
    >({
      query: ({ sessionId, entry }) => ({
        url: `/api/grading/v1/sessions/${sessionId}/entries/${entry.id}`,
        method: "PUT",
        body: entry,
      }),
      transformResponse: (r: ApiResponse<EntryResponse>) => r.data,
      invalidatesTags: (_r, _e, { sessionId }) => [
        { type: "Session", id: sessionId },
      ],
    }),
    deleteEntry: builder.mutation<void, { sessionId: string; entryId: string }>(
      {
        query: ({ sessionId, entryId }) => ({
          url: `/api/grading/v1/sessions/${sessionId}/entries/${entryId}`,
          method: "DELETE",
        }),
        invalidatesTags: (_r, _e, { sessionId }) => [
          { type: "Session", id: sessionId },
        ],
      },
    ),
    reassignEntry: builder.mutation<
      EntryResponse,
      { sessionId: string; entryId: string; studentId: string }
    >({
      query: ({ sessionId, entryId, studentId }) => ({
        url: `/api/grading/v1/sessions/${sessionId}/entries/${entryId}/reassign`,
        method: "POST",
        body: { studentId },
      }),
      transformResponse: (r: ApiResponse<EntryResponse>) => r.data,
      invalidatesTags: (_r, _e, { sessionId }) => [
        { type: "Session", id: sessionId },
      ],
    }),
    getReview: builder.query<ReviewView, string>({
      query: (id) => `/api/grading/v1/sessions/${id}/review`,
      transformResponse: (r: ApiResponse<ReviewView>) => r.data,
      providesTags: (_r, _e, id) => [{ type: "Session", id }],
    }),
    resolveFlag: builder.mutation<
      ReviewFlag,
      {
        sessionId: string;
        flagId: string;
        status: "resolved" | "dismissed";
        keepEntryId?: string;
      }
    >({
      query: ({ sessionId, flagId, status, keepEntryId }) => ({
        url: `/api/grading/v1/sessions/${sessionId}/flags/${flagId}`,
        method: "PATCH",
        body: { status, keepEntryId },
      }),
      transformResponse: (r: ApiResponse<{ flag: ReviewFlag }>) => r.data.flag,
      invalidatesTags: (_r, _e, { sessionId }) => [
        { type: "Session", id: sessionId },
      ],
    }),
    recomputeFlags: builder.mutation<{ openCount: number }, string>({
      query: (sessionId) => ({
        url: `/api/grading/v1/sessions/${sessionId}/flags/recompute`,
        method: "POST",
      }),
      transformResponse: (r: ApiResponse<{ openCount: number }>) => r.data,
      invalidatesTags: (_r, _e, sessionId) => [
        { type: "Session", id: sessionId },
      ],
    }),
    getStudentResult: builder.query<
      StudentResult & {
        finalized: boolean;
        scoreId: string | null;
        priorWindow: PriorWindow | null;
        documentIds: Record<string, string | null> | null;
      },
      { sessionId: string; studentId: string }
    >({
      query: ({ sessionId, studentId }) =>
        `/api/grading/v1/sessions/${sessionId}/results/${studentId}`,
      transformResponse: (
        r: ApiResponse<{
          result: StudentResult & {
            finalized: boolean;
            scoreId: string | null;
            priorWindow: PriorWindow | null;
            documentIds: Record<string, string | null> | null;
          };
        }>,
      ) => r.data.result,
      providesTags: (_r, _e, { sessionId }) => [
        { type: "Session", id: sessionId },
      ],
    }),
    getAudit: builder.query<Record<string, unknown>[], string>({
      query: (id) => `/api/grading/v1/sessions/${id}/audit`,
      transformResponse: (
        r: ApiResponse<{ audit: Record<string, unknown>[] }>,
      ) => r.data.audit,
      providesTags: (_r, _e, id) => [{ type: "Session", id }],
    }),
    generateJoinCode: builder.mutation<
      { code: string; expiresAt: string; qrPayload: string },
      string
    >({
      query: (id) => ({
        url: `/api/grading/v1/sessions/${id}/join-code`,
        method: "POST",
      }),
      transformResponse: (
        r: ApiResponse<{ code: string; expiresAt: string; qrPayload: string }>,
      ) => r.data,
      invalidatesTags: (_r, _e, id) => [{ type: "Session", id }],
    }),
    revokeJoinCode: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/grading/v1/sessions/${id}/join-code`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, id) => [{ type: "Session", id }],
    }),
    /** Unauthenticated: a volunteer exchanges the code for a scorer token (web station). */
    joinSession: builder.mutation<
      {
        sessionToken: string;
        expiresAt: string;
        session: {
          id: string;
          name: string;
          date: string;
          status: string;
          benchmarkVersion: number | null;
          stations: Station[];
        };
      },
      { code: string; stationId?: string; displayName?: string }
    >({
      query: (body) => ({
        url: "/api/grading/v1/sessions/join",
        method: "POST",
        body,
      }),
      transformResponse: (r: ApiResponse<any>) => r.data,
    }),
    getBenchmarks: builder.query<
      { version: number; rows: BenchmarkRow[] },
      { version?: number; date?: string } | void
    >({
      query: (q) => {
        const params = new URLSearchParams();
        if (q?.version) params.set("version", String(q.version));
        if (q?.date) params.set("date", q.date);
        return `/api/grading/v1/benchmarks?${params.toString()}`;
      },
      transformResponse: (
        r: ApiResponse<{ version: number; rows: BenchmarkRow[] }>,
      ) => r.data,
      providesTags: ["Benchmarks"],
    }),
  }),
});

export const {
  useListSessionsQuery,
  useGetSessionQuery,
  useLazyGetSessionQuery,
  useCreateSessionMutation,
  useTransitionSessionMutation,
  useSetStudentStatusMutation,
  useUpdateStationMutation,
  usePutEntryMutation,
  useDeleteEntryMutation,
  useReassignEntryMutation,
  useGetReviewQuery,
  useGetStudentResultQuery,
  useResolveFlagMutation,
  useRecomputeFlagsMutation,
  useGetAuditQuery,
  useGenerateJoinCodeMutation,
  useRevokeJoinCodeMutation,
  useJoinSessionMutation,
  useGetBenchmarksQuery,
} = sessionsApiSlice;
